import React, { Component, Fragment } from 'react';

import { Form } from 'components/ClubComponents';
import { Loading } from 'components/cosmos';
import Icon from 'components/icons';
import { I18n } from 'components/translate';
import { to } from 'helpers/Async';
import { Converter } from 'helpers/Object';
import { isBlank } from 'helpers/String';
import equals from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { CommunityService } from 'modules/community';
import ConfigService from 'modules/configManager/ConfigService';
import ContentPreventLeave from 'modules/content/components/ContentPreventLeave';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';

import AutomatedCommentReplyConfiguration from './AutomatedCommentReplyConfiguration';
import OptionsConfiguration from './OptionsConfiguration';
import PersonaConfiguration from './PersonaConfiguration';

const VALIDATION_SCHEMA = {
  personaName: [
    {
      name: 'custom-required-name',
      when: 'onChange',
      handler: ({ value, fields }) => {
        if (!fields.personaActive) {
          return true;
        }

        return !isBlank(value);
      }
    }
  ]
};

export default class Configuration extends Component {
  static propTypes = {
    t: PropTypes.func
  };

  constructor(props) {
    super(props);

    this.toggleLoading = this.toggleLoading.bind(this);
    this.beforeSavingConfiguration = this.beforeSavingConfiguration.bind(this);
    this.onCancelSaveOptionsPreventLeave =
      this.onCancelSaveOptionsPreventLeave.bind(this);

    this.state = {
      options: {},
      loading: true,
      existsBasicData: false,
      configurationPreventLeave: false,
      valid: false
    };

    this.formRef = React.createRef(null);

    this.initial = {
      commentForAllClasses: false,
      instantTopicApproval: false,
      instantReplyApproval: false,
      active: false,
      personaActive: false,
      personaName: '',
      personaAvatar: null
    };

    this.changed = false;
    this.unmounted = false;

    this.converter = Converter([
      'active',
      'commentForAllClasses',
      'instantReplyApproval',
      'instantTopicApproval',
      {
        origin: 'personaActive',
        destination: 'personaConfiguration.enabled'
      },
      {
        origin: 'personaAvatar',
        destination: 'personaConfiguration.persona.avatarFileKey',
        toDestination: {
          conditions: ({ personaActive }) => personaActive
        }
      },
      {
        origin: 'personaName',
        destination: 'personaConfiguration.persona.name',
        toDestination: {
          conditions: ({ personaActive }) => personaActive
        }
      }
    ]);
  }

  async componentDidMount() {
    this.fetchExistsBasicData();

    const [error, persistedOptions] = await to(
      CommunityService.getConfigurationData()
    );

    if (this.unmounted) {
      return;
    }

    if (!isNil(error)) {
      UiService.setAlertError('error_try_again');
      this.setState({
        loading: false
      });

      return;
    }

    const convertedPersistedOptions =
      this.converter.asOriginObject(persistedOptions);
    const options = { ...this.initial, ...convertedPersistedOptions };

    this.initial = { ...options };

    this.setState({ options: { ...options }, loading: false });
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  async fetchExistsBasicData() {
    const basicParameter = 'MASS_COMMENT';
    const [error, existsBasicData] = await to(
      ConfigService.existsBasicData(basicParameter)
    );

    if (this.unmounted) {
      return;
    }

    if (!isNil(error)) {
      UiService.setAlertError('error_try_again');
      this.setState({
        loading: false
      });

      return;
    }

    this.setState({ existsBasicData });
  }

  toggleLoading() {
    this.setState((prevState) => ({ loading: !prevState.loading }));
  }

  save = async () => {
    const form = this.formRef.current;

    form && form.submit();
  };

  handleSubmit = async () => {
    const form = this.formRef.current;
    const options = this.converter.asDestinationObject(this.state.options);

    this.toggleLoading();

    const [error] = await to(CommunityService.saveConfigurationData(options));

    if (!isNil(error)) {
      UiService.setAlertError('error_try_again');
      this.setState({
        loading: false
      });

      return;
    }

    // resetting form initial and changing form key to force update
    this.initial = { ...this.state.options };
    this.changed = false;

    UiService.setAlertSuccess('community.configurations_save_success_message');

    this.setState(
      {
        loading: false,
        configurationPreventLeave: false
      },
      () => {
        form && form.reset();
      }
    );
  };

  handleChange = ({ fields, valid }) => {
    this.changed = !equals(fields, this.initial);

    this.setState({
      valid,
      options: { ...fields }
    });
  };

  beforeSavingConfiguration() {
    const { options } = this.state;
    const isDirty = ContentPreventLeave.isDirty(this.initial, options);

    if (!isDirty) {
      this.handleSubmit();
    } else {
      this.setState({ configurationPreventLeave: true });
    }
  }

  onCancelSaveOptionsPreventLeave() {
    this.setState({ configurationPreventLeave: false });
  }

  renderForm = ({ errors, fields, onChange }) => {
    const { existsBasicData } = this.state;

    return (
      <React.Fragment>
        <OptionsConfiguration fields={fields} onChange={onChange} />
        <PersonaConfiguration
          errors={errors}
          fields={fields}
          onChange={onChange}
        />
        {existsBasicData && <AutomatedCommentReplyConfiguration />}
      </React.Fragment>
    );
  };

  renderPreventLeave() {
    const { configurationPreventLeave, options } = this.state;

    return (
      <Fragment>
        <ContentPreventLeave
          snapshot={this.initial}
          current={options}
          onConfirm={this.beforeSavingConfiguration}
        />
        {configurationPreventLeave &&
          ContentPreventLeave.renderModal({
            onConfirm: this.handleSubmit,
            onCancel: this.onCancelSaveOptionsPreventLeave
          })}
      </Fragment>
    );
  }

  render() {
    const { loading, valid } = this.state;

    return (
      <div className="community-configuration _position-relative">
        {!loading && (
          <React.Fragment>
            <Form
              key="form"
              ref={this.formRef}
              className="_w-full _m-0"
              onSubmit={this.handleSubmit}
              onChange={this.handleChange}
              initial={this.initial}
              schema={VALIDATION_SCHEMA}
            >
              {this.renderForm}
            </Form>
            <button
              className="hot-button hot-button--primary hot-button--lg"
              data-test="btn-save-configuration"
              disabled={!this.changed || (this.changed && !valid) || loading}
              onClick={this.save}
            >
              <Icon prefix="fal" iconName="check" className="_mr-2" />
              <I18n label="general.save" />
            </button>
          </React.Fragment>
        )}
        {loading && <Loading center />}
        {this.renderPreventLeave()}
      </div>
    );
  }
}
